import { InfluencersProfileFormat, ProposalDownloadFormat } from '@anytag/graphql';
import { ButtonDropdown, DownloadSubmenuIcon } from '@anytag/shared-atoms';
import { useDownloadFile, useQueryHelper, useToggle } from '@anytag/shared-hooks';
import { DownloadFileType, DropdownOption, MenuTypeLevel } from '@anytag/shared-types';
import { cn, getRestApiUrl } from '@anytag/shared-utils';
import { GoogleAuthDialog, useGoogleAuthDownload } from '@anytag/shared-molecules';
import { useProposeInfluencersApi } from '../hooks';

interface ProposeInfluencersDownloadButtonProps {
  disabled?: boolean;
  className?: string;
  selectedInfluencerIds?: number[];
}

export const ProposeInfluencersDownloadButton = ({
  disabled,
  className,
  selectedInfluencerIds = []
}: ProposeInfluencersDownloadButtonProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { on: downloading, setOn, setOff } = useToggle();
  const { callRequestUserProposalExcel, callRequestUserProposalPptx } = useProposeInfluencersApi();
  const { handleDownloadFile, downloading: csvDownloading } = useDownloadFile();
  const {
    canDownloadGoogleReport,
    handleInfluencerProposalSpreadsheetDownload,
    dialogProps,
    handleInfluencerProposalGoogleSlideDownload
  } = useGoogleAuthDownload({
    goBackUrl: '/influencers/proposal',
    influencerIds: selectedInfluencerIds
  });

  const downloadOptions: DropdownOption[] = [
    {
      value: 'Profile Sheet',
      menuType: MenuTypeLevel.MULTI_LEVEL,
      label: t<string>('Profile Sheet'),
      submenu: [
        {
          label: t<string>('PowerPoint'),
          value: DownloadFileType.PPTX,
          prefixIcon: <DownloadSubmenuIcon icon="powerPoint" />
        },
        ...(canDownloadGoogleReport
          ? [
              {
                label: t<string>('Google Slide'),
                value: DownloadFileType.GOOGLE_SLIDE,
                prefixIcon: <DownloadSubmenuIcon icon="googleSlide" />
              }
            ]
          : [])
      ]
    },
    {
      value: 'Proposal List',
      menuType: MenuTypeLevel.MULTI_LEVEL,
      label: t('Proposal List'),
      submenu: [
        {
          label: t<string>('CSV'),
          value: DownloadFileType.CSV,
          prefixIcon: <DownloadSubmenuIcon icon="csv" />
        },
        {
          label: t<string>('Excel'),
          value: DownloadFileType.EXCEL,
          prefixIcon: <DownloadSubmenuIcon icon="excel" />
        },
        ...(canDownloadGoogleReport
          ? [
              {
                label: t<string>('Spreadsheet'),
                value: DownloadFileType.SPREADSHEET,
                prefixIcon: <DownloadSubmenuIcon icon="spreadsheet" />
              }
            ]
          : [])
      ]
    }
  ];

  const handleDownloadOptionSelected = async (option: DropdownOption) => {
    setOn();

    try {
      switch (option.value) {
        case DownloadFileType.CSV:
          await handleDownloadFile({
            url: getRestApiUrl('user/proposal/csv/'),
            filenamePrefix: 'propose_influencers',
            params: { ids: selectedInfluencerIds }
          });
          break;
        case DownloadFileType.PPTX:
          await callRequestUserProposalPptx({
            variables: {
              format: InfluencersProfileFormat.PPTX,
              influencerIds: selectedInfluencerIds
            }
          });
          enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
          break;
        case DownloadFileType.EXCEL: {
          const { data } = await callRequestUserProposalExcel({
            variables: { influencerIds: selectedInfluencerIds, format: ProposalDownloadFormat.EXCEL }
          });
          if (data?.requestUserProposalExcel?.ok) {
            enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
          } else {
            enqueueSnackbar(t('failedToDownload'), { variant: 'error' });
          }
          break;
        }
        // Google Auth required for Slide and Spreadsheet
        case DownloadFileType.SPREADSHEET:
          handleInfluencerProposalSpreadsheetDownload();
          break;
        case DownloadFileType.GOOGLE_SLIDE:
          handleInfluencerProposalGoogleSlideDownload();
          break;

        default:
          break;
      }
    } catch (error) {
      enqueueSnackbar(t('failedToDownload'), { variant: 'error' });
    } finally {
      setOff();
    }
  };

  return (
    <>
      <ButtonDropdown
        variant="outline"
        align="end"
        side="bottom"
        disabled={disabled}
        loading={downloading || csvDownloading}
        className={cn('ml-2 min-w-33', className)}
        options={downloadOptions}
        title={t('Button.Download')}
        onSelectOption={handleDownloadOptionSelected}
        prefixIcon={{ icon: 'download', size: '14px' }}
      />
      <GoogleAuthDialog {...dialogProps} />
    </>
  );
};
